<i18n src="@/locales/modules/RequestTypeTranslate.json"></i18n>

<template>
	<div class="request-type-config">
		<div class="header request-type-config__header flex justify-between mb-10 flex-wrap">
			<div class="header__navigation flex items-center">
				<i class="nucleo nucleo-arrow-left text-black text-3xl mr-6 sm:mr-12 cursor-pointer" @click.once="$router.go(-1)"></i>
				<h2>{{ $t("RequestTypeConfig") }} - {{ parentStepData.name }}</h2>
			</div>

			<vs-button type="border" class="w-48 font-bold ml-auto mt-3 sm:ml-0 sm:mt-0" @click="toggleRequestTypeStatisticalExpressionDialog(true)">{{ $t("StatisticalExpression") }}</vs-button>
		</div>

		<cbs-dynamic-form-config
			:class="[
				'parent-form request-type-config__parent-form m-auto my-6 max-w-3xl',
				{ 'parent-form--empty': !parentStepData || (parentStepData.controls && parentStepData.controls.length === 0) }
			]"
			v-if="parentStepData && parentStepData.controls"
			v-model="parentStepData.controls"
			:isEditable="false"
			:title="$t('Form')"
			:isShowToggleMenu="false"
			:fixedFields="fixedFields"
			:isCanControlField="parentStepData && parentStepData.can_update"
			@onClickAddField="handleClickAddStepField(parentStepData)"
			@onSortField="sortData => handleSortStepField(parentStepData, sortData)"
			@onClickField="fieldData => handleClickStepField(parentStepData, fieldData)"
			@onClickDeleteField="fieldData => handleClickDeleteStepField(parentStepData.id, fieldData)"
		/>

		<div v-if="childStepsData && childStepsData.length > 0">
			<cbs-dynamic-form-config
				class="request-type-config__child-form m-auto my-6 max-w-3xl"
				v-for="step in childStepsData"
				v-model="step.controls"
				:key="step.id"
				:title="step.name"
				:isEditable="parentStepData && parentStepData.can_update"
				:isDeleteable="parentStepData && parentStepData.can_delete"
				:isCanControlField="parentStepData && parentStepData.can_update"
				@onClickEdit="handleClickEditStep(step)"
				@onClickDelete="handleClickDeleteStep(step.id)"
				@onClickAddField="handleClickAddStepField(step)"
				@onClickViewDetail="handleClickViewDetailStep(step)"
				@onSortField="sortData => handleSortStepField(step, sortData)"
				@onClickField="fieldData => handleClickStepField(step, fieldData)"
				@onClickDeleteField="fieldData => handleClickDeleteStepField(step.id, fieldData)"
			/>
		</div>

		<div class="footer request-type-config__footer flex justify-center items-center m-auto max-w-3xl">
			<span
				class="footer__btn-add text-lg text-gray-2 rounded border border-dashed border-gray-4 px-3 py-1 w-full text-center cursor-pointer hover:border-gray-3 hover:font-bold"
				v-if="parentStepData && parentStepData.can_update"
				@click="handleClickAddStep"
				>{{ $t("CreateStep") }}</span
			>
		</div>

		<config-field-dialog
			v-if="isShowRequestTypeFieldDialog"
			v-model="requestTypeFieldData.value"
			:isEditable="true"
			:title="requestTypeFieldData.title"
			:formFields="requestTypeFieldData.formFields"
			:isDialogActive="isShowRequestTypeFieldDialog"
			@closeDialog="toggleRequestTypeFieldDialog"
			@onSubmit="handleCreateOrUpdateRequestTypeField"
		/>

		<request-type-step-dialog
			v-if="isShowRequestTypeStepDialog"
			v-model="requestTypeStepData.value"
			:title="requestTypeStepData.title"
			:isEditable="parentStepData && parentStepData.can_update"
			:isDialogActive="isShowRequestTypeStepDialog"
			@closeDialog="toggleRequestTypeStepDialog"
			@onSubmit="handleCreateOrUpdateRequestTypeStep"
		/>

		<request-type-statistical-expression-dialog
			v-if="isShowRequestTypeStatisticalExpressionDialog"
			:isEditable="parentStepData && parentStepData.can_update"
			:value="parentStepData.statistic_expressions || []"
			:isDialogActive="isShowRequestTypeStatisticalExpressionDialog"
			@closeDialog="toggleRequestTypeStatisticalExpressionDialog"
			@onClickAddNew="handleClickAddNewStatisticalExpressionData"
			@onClickEditItem="handleClickEditStatisticalExpressionData"
			@onClickDeleteItem="handleClickDeleteStatisticalExpressionData"
		/>

		<request-type-statistical-expression-form-dialog
			v-if="isShowRequestTypeStatisticalExpressionFormDialog"
			v-model="requestTypeStatisticalExpressionData"
			:formFields="combinedFormFields"
			:isDialogActive="isShowRequestTypeStatisticalExpressionFormDialog"
			@onSubmit="handleCreateOrUpdateStatisticalExpression"
			@closeDialog="toggleRequestTypeStatisticalExpressionFormDialog"
		/>
	</div>
</template>

<script>
import _ from "lodash";
import { mapGetters } from "vuex";
import { getUserPermissions, requestTypePermissions } from "@/mixins/mixins.js";
import moduleRequestTypeConfig from "@/store/request-type-config/moduleRequestTypeConfig.js";

import RequestTypeStepDialog from "./RequestTypeStepDialog.vue";
import CbsDynamicFormConfig from "@/components/cbs-dynamic-form-config/CbsDynamicFormConfig.vue";
import ConfigFieldDialog from "@/components/cbs-dynamic-form-config/ConfigFieldDialog.vue";
import RequestTypeStatisticalExpressionDialog from "./RequestTypeStatisticalExpressionDialog.vue";
import RequestTypeStatisticalExpressionFormDialog from "./RequestTypeStatisticalExpressionFormDialog.vue";

const initRequestTypeFieldData = {
	id: null,
	title: "",
	children: [],
	control: null,
	attributes: null,
	is_optional: false,
	enable: true,
	description: {
		value: "",
		enable: false,
		position: null
	}
};

const initRequestTypeStepData = {
	id: null,
	name: "",
	config: {
		approversConfig: {
			content: "",
			relations: []
		},
		approvedNotificationConfig: {
			content: "",
			relations: []
		},
		rejectedNotificationConfig: {
			content: "",
			relations: []
		},
		description: ""
	}
};

const initRequestTypeStatisticalExpressionData = {
	id: null,
	name: "",
	typeId: null,
	expression: ""
};

export default {
	name: "RequestTypeConfig",
	mixins: [getUserPermissions(requestTypePermissions)],
	components: {
		ConfigFieldDialog,
		CbsDynamicFormConfig,
		RequestTypeStepDialog,
		RequestTypeStatisticalExpressionDialog,
		RequestTypeStatisticalExpressionFormDialog
	},
	data() {
		return {
			requestTypeFieldData: null,
			isShowRequestTypeFieldDialog: false,

			requestTypeStepData: null,
			isShowRequestTypeStepDialog: false,

			isShowRequestTypeStatisticalExpressionDialog: false,

			requestTypeStatisticalExpressionData: null,
			isShowRequestTypeStatisticalExpressionFormDialog: false
		};
	},
	computed: {
		...mapGetters({
			parentStepData: "requestTypeConfig/getParentStepData",
			childStepsData: "requestTypeConfig/getChildStepsData"
		}),
		combinedFormFields() {
			return this.parentStepData.controls
				.concat(...this.childStepsData.map(step => step.controls))
				.filter(field => [this.$constant.request.control.datetime, this.$constant.request.control.number].includes(field.control) && field.id);
		},
		fixedFields() {
			return [
				{
					is_optional: false,
					title: this.$t("Purpose"),
					control: this.$constant.request.control.text
				}
			];
		}
	},
	methods: {
		async fetchRequestTypeData() {
			try {
				this.$vs.loading();
				await this.$store.dispatch("requestTypeConfig/getRequestType", { id: this.$route.params.id, list_all_controls: true });
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$nextTick(() => this.$vs.loading.close());
			}
		},
		toggleRequestTypeStepDialog(val = false) {
			this.isShowRequestTypeStepDialog = val;
		},
		handleClickAddStep() {
			this.requestTypeStepData = {
				title: this.$t("CreateStep"),
				value: _.cloneDeep(initRequestTypeStepData)
			};
			this.toggleRequestTypeStepDialog(true);
		},
		handleClickEditStep(stepData) {
			const cloneStepData = _.cloneDeep(stepData);

			const approversConfig = _.get(cloneStepData, "notifications.approvers") || {};
			approversConfig.content = approversConfig.hashtag_content || "";

			const approvedNotificationConfig = _.get(cloneStepData, "notifications.approve_notifications") || {};
			approvedNotificationConfig.content = approvedNotificationConfig.hashtag_content || "";

			const rejectedNotificationConfig = _.get(cloneStepData, "notifications.reject_notifications") || {};
			rejectedNotificationConfig.content = rejectedNotificationConfig.hashtag_content || "";
			const description = cloneStepData.description ? cloneStepData.description : "";
			const approverFillInfo = cloneStepData.approverFillInfo ? cloneStepData.approverFillInfo : false;
			const color = cloneStepData.color ? cloneStepData.color : null;
			this.requestTypeStepData = {
				title: this.$t("UpdateStep", { stepName: stepData.name }),
				value: {
					id: cloneStepData.id,
					name: cloneStepData.name,
					config: {
						approversConfig,
						approvedNotificationConfig,
						rejectedNotificationConfig,
						description,
						approverFillInfo,
						color
					}
				}
			};
			this.toggleRequestTypeStepDialog(true);
		},
		handleClickViewDetailStep(stepData) {
			const cloneStepData = _.cloneDeep(stepData);
			this.requestTypeStepData = {
				title: this.$t("DetailStep", { stepName: stepData.name }),
				value: {
					id: cloneStepData.id,
					name: cloneStepData.name,
					config: {
						approversConfig: _.get(cloneStepData, "notifications.approvers") || {},
						approvedNotificationConfig: _.get(cloneStepData, "notifications.approve_notifications") || {},
						rejectedNotificationConfig: _.get(cloneStepData, "notifications.reject_notifications") || {}
					}
				}
			};
			this.toggleRequestTypeStepDialog(true);
		},
		async handleClickDeleteStep(stepId) {
			try {
				this.$vs.loading();
				await this.$store.dispatch("requestTypeConfig/deleteRequestTypeStep", stepId);
				this.$helper.notifySuccess({
					context: this,
					options: {
						text: this.$t("DeleteStepSuccessful")
					}
				});
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$nextTick(() => this.$vs.loading.close());
			}
		},
		async handleCreateOrUpdateRequestTypeStep() {
			try {
				this.$vs.loading();
				const isEdit = !!this.requestTypeStepData.value.id;
				const payload = {
					name: _.get(this.requestTypeStepData.value, "name"),
					notifications: {
						approvers: this.formatStepConfigData(_.get(this.requestTypeStepData.value, "config.approversConfig")),
						reject_notifications: this.formatStepConfigData(_.get(this.requestTypeStepData.value, "config.rejectedNotificationConfig")),
						approve_notifications: this.formatStepConfigData(_.get(this.requestTypeStepData.value, "config.approvedNotificationConfig"))
					},
					description: _.get(this.requestTypeStepData.value, "config.description"),
					approverFillInfo: _.get(this.requestTypeStepData.value, "config.approverFillInfo"),
					color: _.get(this.requestTypeStepData.value, "config.color")
				};

				if (isEdit) {
					payload.id = _.get(this.requestTypeStepData.value, "id");
					await this.$store.dispatch("requestTypeConfig/updateRequestTypeStep", payload);
				} else {
					payload.req_type_id = this.parentStepData.id;
					await this.$store.dispatch("requestTypeConfig/addRequestTypeStep", payload);
				}
				this.$helper.notifySuccess({
					context: this,
					options: {
						text: this.$t(`${isEdit ? "UpdateStepSuccessful" : "CreateStepSuccessful"}`)
					}
				});
				this.toggleRequestTypeStepDialog(false);
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$nextTick(() => this.$vs.loading.close());
			}
		},
		formatStepConfigData(configData) {
			return {
				content: _.get(configData, "content"),
				relations: (_.get(configData, "relations") || []).map(option => ({ id: option.id, type: option.type }))
			};
		},
		handleClickStepField(stepData, stepFieldData) {
			const requestTypeFieldTitle =
				stepData.name === this.parentStepData.name ? this.$t("UpdateStepField", { stepName: this.$t("Form") }) : this.$t("UpdateStepField", { stepName: stepData.name });
			this.requestTypeFieldData = {
				stepId: stepData.id,
				title: requestTypeFieldTitle,
				formFields: stepData.controls || [],
				value: _.cloneDeep(stepFieldData)
			};
			this.toggleRequestTypeFieldDialog(true);
		},
		async handleClickDeleteStepField(stepId, fieldData) {
			try {
				this.$vs.loading();
				await this.$store.dispatch("requestTypeConfig/deleteDynamicFormField", { stepId, id: fieldData.id });
				this.$helper.notifySuccess({
					context: this,
					options: {
						text: this.$t("DeleteFieldSuccessful")
					}
				});
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$nextTick(() => this.$vs.loading.close());
			}
		},
		handleClickAddStepField(stepData) {
			const requestTypeFieldTitle =
				stepData.name === this.parentStepData.name ? this.$t("CreateStepField", { stepName: this.$t("Form") }) : this.$t("CreateStepField", { stepName: stepData.name });
			this.requestTypeFieldData = {
				stepId: stepData.id,
				title: requestTypeFieldTitle,
				formFields: stepData.controls || [],
				value: {
					...initRequestTypeFieldData,
					description: {
						...initRequestTypeFieldData.description,
						position: this.$constant.DYNAMIC_FORM_FIELD_INFO_POSITION.BOTTOM
					}
				}
			};
			this.toggleRequestTypeFieldDialog(true);
		},
		toggleRequestTypeFieldDialog(val = false) {
			this.isShowRequestTypeFieldDialog = val;
		},
		async handleCreateOrUpdateRequestTypeField() {
			try {
				this.$vs.loading();
				const isEdit = !!this.requestTypeFieldData.value.id;
				const isParentStep = this.requestTypeFieldData.stepId === this.parentStepData.id;
				const payload = {
					formable_id: this.requestTypeFieldData.stepId,
					id: _.get(this.requestTypeFieldData.value, "id"),
					title: _.get(this.requestTypeFieldData.value, "title"),
					control: _.get(this.requestTypeFieldData.value, "control"),
					attributes: _.get(this.requestTypeFieldData.value, "attributes"),
					is_optional: _.get(this.requestTypeFieldData.value, "is_optional"),
					enable: _.get(this.requestTypeFieldData.value, "enable"),
					children: JSON.stringify(_.get(this.requestTypeFieldData.value, "children") || []),
					description: _.omitBy(_.get(this.requestTypeFieldData.value, "description") || {}, (item, key) => key === "__typename")
				};

				if (!isParentStep) {
					payload.formable_type = this.$constant.DYNAMIC_FORM_FIELD_MODELS.REQUEST_TYPE_STEP_FIELD;
				} else {
					payload.formable_type = this.$constant.DYNAMIC_FORM_FIELD_MODELS.REQUEST_TYPE_PARENT_FIELD;
				}

				if ([this.$constant.request.control.radio, this.$constant.request.control.checkbox, this.$constant.request.control.dropdown].includes(payload.control)) {
					const payloadAttributes = JSON.parse(payload.attributes);
					payload.attributes = JSON.stringify({
						...payloadAttributes,
						options: payloadAttributes.options.map(option => ({ id: option.id, content: option.content }))
					});
				}

				if (isEdit) {
					await this.$store.dispatch("requestTypeConfig/updateDynamicFormField", {
						payload,
						stepId: this.requestTypeFieldData.stepId
					});
				} else {
					await this.$store.dispatch("requestTypeConfig/addDynamicFormField", {
						payload,
						stepId: this.requestTypeFieldData.stepId
					});
				}
				this.$helper.notifySuccess({
					context: this,
					options: {
						text: this.$t(`${isEdit ? "UpdateFieldSuccessful" : "CreateFieldSuccessful"}`)
					}
				});
				this.toggleRequestTypeFieldDialog(false);
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$nextTick(() => this.$vs.loading.close());
			}
		},
		async handleSortStepField(stepData, sortData) {
			try {
				console.log("onSortField", sortData);
				this.$vs.loading();
				await this.$store.dispatch("requestTypeConfig/sortDynamicFormField", {
					payload: sortData,
					stepId: stepData.id
				});
			} catch (error) {
				this.fetchRequestTypeData();
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$nextTick(() => this.$vs.loading.close());
			}
		},
		toggleRequestTypeStatisticalExpressionDialog(val = false) {
			this.isShowRequestTypeStatisticalExpressionDialog = val;
		},
		toggleRequestTypeStatisticalExpressionFormDialog(val = false) {
			this.isShowRequestTypeStatisticalExpressionFormDialog = val;
		},
		handleClickAddNewStatisticalExpressionData() {
			this.requestTypeStatisticalExpressionData = { ...initRequestTypeStatisticalExpressionData };
			this.toggleRequestTypeStatisticalExpressionFormDialog(true);
		},
		handleClickEditStatisticalExpressionData(statisticalExpressionData) {
			this.requestTypeStatisticalExpressionData = Object.assign(
				{},
				{
					...statisticalExpressionData,
					typeId: Number(statisticalExpressionData.type_object.key)
				}
			);
			this.toggleRequestTypeStatisticalExpressionFormDialog(true);
		},
		async handleClickDeleteStatisticalExpressionData(id) {
			try {
				this.$vs.loading();
				await this.$store.dispatch("requestTypeConfig/deleteRequestTypeStatisticalExpression", id);
				this.$helper.notifySuccess({
					context: this,
					options: {
						text: this.$t("DeleteStatisticalExpressionSuccessful")
					}
				});
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$nextTick(() => this.$vs.loading.close());
			}
		},
		async handleCreateOrUpdateStatisticalExpression() {
			try {
				this.$vs.loading();
				const isEdit = !!this.requestTypeStatisticalExpressionData.id;
				const payload = {
					req_type_id: this.parentStepData.id,
					name: _.get(this.requestTypeStatisticalExpressionData, "name"),
					type: _.get(this.requestTypeStatisticalExpressionData, "typeId"),
					expression: _.get(this.requestTypeStatisticalExpressionData, "expression")
				};

				if (isEdit) {
					(payload.id = _.get(this.requestTypeStatisticalExpressionData, "id")), await this.$store.dispatch("requestTypeConfig/updateRequestTypeStatisticalExpression", payload);
				} else {
					await this.$store.dispatch("requestTypeConfig/addRequestTypeStatisticalExpression", payload);
				}
				this.$helper.notifySuccess({
					context: this,
					options: {
						text: this.$t(`${isEdit ? "UpdateStatisticalExpressionSuccessful" : "CreateStatisticalExpressionSuccessful"}`)
					}
				});
				this.toggleRequestTypeStatisticalExpressionFormDialog(false);
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$nextTick(() => this.$vs.loading.close());
			}
		}
	},
	created() {
		this.$store.registerModule("requestTypeConfig", moduleRequestTypeConfig);
		this.fetchRequestTypeData();
	},
	beforeDestroy() {
		this.$store.commit("requestTypeConfig/RESET_STATE");
		this.$store.unregisterModule("requestTypeConfig");
	}
};
</script>

<style lang="scss" scoped>
.request-type-config {
	top: -52px;
	position: relative;
	background-color: #fff;

	::v-deep .parent-form--empty {
		.footer {
			margin-top: -15px !important;
		}
	}
}

.custom-nested-dialog.request-type-field-dialog {
	&:not(:last-child) {
		background: none !important;
	}
}
</style>
